import { ref, watch, onMounted } from 'vue'

export type ThemeMode = 'auto' | 'light' | 'dark'

const STORAGE_KEY = 'station1-theme'

// Whether StationOne is dark right now: the toggle's data-theme, or the OS when it is on auto.
export const isDarkTheme = (): boolean => {
  const explicit = document.documentElement.getAttribute('data-theme')
  if (explicit === 'dark' || explicit === 'light') return explicit === 'dark'
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false
}

const themeMode = ref<ThemeMode>('auto')

const applyTheme = (mode: ThemeMode) => {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (mode === 'auto') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', mode)
  }
}

export function useTheme() {
  onMounted(() => {
    if (typeof window === 'undefined') return
    const stored = window.localStorage.getItem(STORAGE_KEY) as ThemeMode | null
    if (stored === 'light' || stored === 'dark' || stored === 'auto') {
      themeMode.value = stored
    }
    applyTheme(themeMode.value)
  })

  watch(themeMode, (mode) => {
    applyTheme(mode)
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, mode)
    }
  })

  const setTheme = (mode: ThemeMode) => {
    themeMode.value = mode
  }

  const toggleTheme = () => {
    const order: ThemeMode[] = ['auto', 'light', 'dark']
    const i = order.indexOf(themeMode.value)
    themeMode.value = order[(i + 1) % order.length]!
  }

  return {
    theme: themeMode,
    setTheme,
    toggleTheme,
  }
}
